import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { UserContext } from '../../context/UserContext';
import { usersApi, authApi } from '../../api';
import '../../css/components/UserCard.css';
import HrnbCard from "../controls/HrnbCard";
import { HrnbListGroup, HrnbListGroupItem } from "../controls/HrnbListGroup";
import HrnbLabel from "../controls/HrnbLabel";
import HrnbFormControl from "../controls/HrnbFormControl";
import HrnbButton from "../controls/HrnbButton";
import HrnbAlert from "../controls/HrnbAlert";
import HrnbModal from "../controls/HrnbModal";
import { SaveIcon, DeleteIcon } from "../../icons";
import useUnsavedChanges from '../../hooks/useUnsavedChanges';

/** Оценка сложности пароля */
function getPasswordStrength(password) {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 6) score += 1;
    if (password.length >= 10) score += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    const levels = [
        { score: 0, label: '', color: '' },
        { score: 1, label: 'Слабый', color: '#f44336' },
        { score: 2, label: 'Средний', color: '#ff9800' },
        { score: 3, label: 'Хороший', color: '#4caf50' },
        { score: 4, label: 'Надёжный', color: '#4caf50' },
        { score: 5, label: 'Очень надёжный', color: '#2196f3' },
    ];
    return levels[score] || levels[0];
}

export default function UserCard({ user, onDirtyChange }) {
    const [editedUser, setEditUser] = useState({...user});
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // Снимок исходных данных для сравнения dirty-состояния (username, email)
    const userSnapshotRef = useRef({ username: user.username, email: user.email });
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveSuccess, setSaveSuccess] = useState("");
    const {setCurrentUser, signOut} = useContext(UserContext);
    const [saving, setSaving] = useState(false);
const [updatingPassword, setUpdatingPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Resend confirmation
    const [resendStatus, setResendStatus] = useState(''); // '' | 'sending' | 'sent' | 'error'
    const [resendError, setResendError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const cooldownTimerRef = useRef(null);

    // Delete account
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    // Сброс resend-таймера при размонтировании
    useEffect(() => {
        return () => { if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current); };
    }, []);

    const handleResendConfirmation = useCallback(async () => {
        setResendStatus('sending');
        setResendError('');
        try {
            await authApi.resendConfirmation(editedUser.email);
            setResendStatus('sent');
            // Блокировка на 60 секунд
            setResendCooldown(60);
            cooldownTimerRef.current = setInterval(() => {
                setResendCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(cooldownTimerRef.current);
                        cooldownTimerRef.current = null;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            setResendStatus('error');
            setResendError(err?.response?.data?.message || err?.message || 'Ошибка при отправке письма');
        }
    }, [editedUser.email]);

    const handleDeleteAccount = async () => {
        setDeleting(true);
        setDeleteError('');
        try {
            await usersApi.remove(editedUser.id);
            setDeleteModalOpen(false);
            if (signOut) signOut();
        } catch (err) {
            setDeleteError(err?.response?.data?.message || err?.message || 'Ошибка при удалении аккаунта');
        } finally {
            setDeleting(false);
        }
    };

    const isDirty = useMemo(() => {
        const snap = userSnapshotRef.current;
        const userChanged = editedUser.username !== snap.username || editedUser.email !== snap.email;
        const passwordChanged = newPassword !== '' || confirmPassword !== '';
        return userChanged || passwordChanged;
    }, [editedUser, newPassword, confirmPassword]);

    useUnsavedChanges(isDirty);

    // Уведомляем родителя об изменении dirty-состояния
    useEffect(() => {
        if (onDirtyChange) onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    const validate = () => {
        const errors = {};
        if (!editedUser.username?.trim()) {
            errors.username = 'Имя пользователя не может быть пустым';
        }
        if (!editedUser.email?.trim()) {
            errors.email = 'Email не может быть пустым';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedUser.email)) {
            errors.email = 'Неверный формат email';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Автоисчезновение success-алертов через 3 сек
    const saveSuccessTimerRef = useRef(null);
    const passwordSuccessTimerRef = useRef(null);

    useEffect(() => {
        if (saveSuccess) {
            clearTimeout(saveSuccessTimerRef.current);
            saveSuccessTimerRef.current = setTimeout(() => setSaveSuccess(''), 3000);
        }
        return () => clearTimeout(saveSuccessTimerRef.current);
    }, [saveSuccess]);

    useEffect(() => {
        if (passwordSuccess) {
            clearTimeout(passwordSuccessTimerRef.current);
            passwordSuccessTimerRef.current = setTimeout(() => setPasswordSuccess(''), 3000);
        }
        return () => clearTimeout(passwordSuccessTimerRef.current);
    }, [passwordSuccess]);

    const handleSaveChanges = async () => {
        if (!validate()) return;
        setSaving(true);
        setSaveError("");
        setSaveSuccess("");
        try {
            const requestBody = {
                username: editedUser.username,
                email: editedUser.email
            };
            const updated = await usersApi.update(editedUser.id, requestBody);
            setCurrentUser && setCurrentUser(updated);
            setEditUser(prev => ({...prev, ...updated}));
            // Обновляем снимок — после сохранения это baseline
            userSnapshotRef.current = { username: updated.username, email: updated.email };
            setSaveSuccess('Изменения сохранены');
        } catch (err) {
            console.error('Error: ', err);
            setSaveError(err?.response?.data?.message || err?.message || 'Ошибка при сохранении');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdatePassword = async () => {
        setPasswordError("");
        setPasswordSuccess("");
        if (!newPassword) {
            setPasswordError("Введите пароль");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("Пароли не совпадают");
            return;
        }
        setUpdatingPassword(true);
        try {
            await authApi.updatePassword(newPassword, confirmPassword);
            setNewPassword("");
            setConfirmPassword("");
            setPasswordSuccess('Пароль успешно изменён');
        } catch (err) {
            setPasswordError(err?.response?.data?.message || err?.message || 'Ошибка обновления пароля');
            console.error('Error: ', err);
        } finally {
            setUpdatingPassword(false);
        }
    };

    return (
        <HrnbCard className="user-card">
            <div className="hrnb-card__title">Информация о пользователе: {editedUser.username || '—'}</div>

            {saveSuccess && <HrnbAlert variant="success" onClose={() => setSaveSuccess('')}>{saveSuccess}</HrnbAlert>}
            {saveError && <HrnbAlert variant="danger" onClose={() => setSaveError('')}>{saveError}</HrnbAlert>}
            {validationErrors.username && <HrnbAlert variant="danger" onClose={() => setValidationErrors({})}>{validationErrors.username}</HrnbAlert>}
            {validationErrors.email && <HrnbAlert variant="danger" onClose={() => setValidationErrors({})}>{validationErrors.email}</HrnbAlert>}

            <HrnbListGroup>
                <HrnbListGroupItem>
                    <HrnbLabel text="Имя:">
                        <HrnbFormControl
                            type="text"
                            value={editedUser.username || ''}
                            onChange={e => {
                                setEditUser(prev => ({...prev, username: e.target.value}));
                                setValidationErrors(prev => ({...prev, username: ''}));
                            }}
                        />
                    </HrnbLabel>
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <HrnbLabel text="Email:">
                        <HrnbFormControl
                            type="email"
                            value={editedUser.email || ''}
                            onChange={e => {
                                setEditUser(prev => ({...prev, email: e.target.value}));
                                setValidationErrors(prev => ({...prev, email: ''}));
                            }}
                        />
                    </HrnbLabel>
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <strong>Роль:</strong> {user.role}
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <strong>Email подтверждён:</strong>{" "}
                    {user.emailVerified
                        ? <span style={{ color: '#4caf50' }}>Да</span>
                        : <span style={{ color: '#f44336' }}>Нет</span>}
                    {!user.emailVerified && (
                        <div style={{ marginTop: 8 }}>
                            <HrnbButton
                                variant="outline-warning"
                                size="sm"
                                onClick={handleResendConfirmation}
                                disabled={resendStatus === 'sending' || resendCooldown > 0}
                            >
                                {resendStatus === 'sending'
                                    ? 'Отправка...'
                                    : resendCooldown > 0
                                        ? `Повторно через ${resendCooldown}с`
                                        : 'Выслать письмо подтверждения'}
                            </HrnbButton>
                            {resendStatus === 'sent' && (
                                <div style={{ marginTop: 8 }}>
                                    <HrnbAlert type="success">✓ Письмо отправлено</HrnbAlert>
                                </div>
                            )}
                            {resendError && (
                                <div style={{ marginTop: 8 }}>
                                    <HrnbAlert type="danger">{resendError}</HrnbAlert>
                                </div>
                            )}
                        </div>
                    )}
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <strong>Активность:</strong> {user.isActive ? 'Активен' : 'Заблокирован'}
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <strong>Зарегистрирован:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleString() : '-'}
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <strong>Последнее
                        изменение:</strong> {user.updatedAt ? new Date(user.updatedAt).toLocaleString() : '-'}
                </HrnbListGroupItem>
            </HrnbListGroup>

            <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                <HrnbButton variant="primary" onClick={handleSaveChanges} icon={<SaveIcon />} disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить изменения'}
                </HrnbButton>
            </div>

            <hr style={{ margin: '24px 0 16px', borderColor: '#2e3643' }} />
            <h4 style={{ margin: '0 0 16px', color: '#e0e0e0', fontSize: '16px', fontWeight: 600 }}>Смена пароля</h4>
            {passwordSuccess && <HrnbAlert variant="success" onClose={() => setPasswordSuccess('')}>{passwordSuccess}</HrnbAlert>}
            {passwordError && <HrnbAlert variant="danger" onClose={() => setPasswordError('')}>{passwordError}</HrnbAlert>}
            <HrnbListGroup>
                <HrnbListGroupItem>
                    <HrnbLabel text="Новый пароль:">
                        <HrnbFormControl
                            type="password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                        />
                        {newPassword && (() => {
                            const strength = getPasswordStrength(newPassword);
                            return (
                                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ display: 'flex', gap: 2, width: 100 }}>
                                        {[1,2,3,4,5].map(i => (
                                            <div key={i} style={{
                                                flex: 1,
                                                height: 4,
                                                borderRadius: 2,
                                                background: i <= strength.score ? strength.color : '#2e3643',
                                                transition: 'background 0.2s',
                                            }} />
                                        ))}
                                    </div>
                                    <span style={{ fontSize: 12, color: strength.color }}>{strength.label}</span>
                                </div>
                            );
                        })()}
                    </HrnbLabel>
                    <HrnbLabel text="Подтверждение пароля:" style={{ marginTop: '12px' }}>
                        <HrnbFormControl
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                        />
                    </HrnbLabel>
                    <HrnbButton variant="warning" onClick={handleUpdatePassword} style={{ marginTop: '20px' }}
                                disabled={updatingPassword || !newPassword || !confirmPassword}>
                        {updatingPassword ? 'Обновление...' : 'Сменить пароль'}
                    </HrnbButton>
                </HrnbListGroupItem>
            </HrnbListGroup>

            <hr style={{ margin: '24px 0 16px', borderColor: '#2e3643' }} />
            <h4 style={{ margin: '0 0 16px', color: '#f44336', fontSize: '16px', fontWeight: 600 }}>Опасная зона</h4>
            {deleteError && <HrnbAlert variant="danger" onClose={() => setDeleteError('')}>{deleteError}</HrnbAlert>}
            <HrnbButton
                variant="outline-danger"
                size="sm"
                icon={<DeleteIcon />}
                onClick={() => setDeleteModalOpen(true)}
            >
                Удалить аккаунт
            </HrnbButton>

            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление аккаунта"
            >
                <p style={{ color: '#e0e0e0' }}>
                    Вы уверены, что хотите удалить свой аккаунт? Это действие <strong>нельзя отменить</strong>.
                    Все ваши данные будут удалены без возможности восстановления.
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                    <HrnbButton variant="outline" onClick={() => setDeleteModalOpen(false)}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton variant="danger" onClick={handleDeleteAccount} disabled={deleting}>
                        {deleting ? 'Удаление...' : 'Удалить навсегда'}
                    </HrnbButton>
                </div>
            </HrnbModal>
        </HrnbCard>
    );
}

UserCard.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        username: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        role: PropTypes.string,
        isActive: PropTypes.bool,
        emailVerified: PropTypes.bool,
        createdAt: PropTypes.string,
        updatedAt: PropTypes.string,
        profiles: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                displayName: PropTypes.string,
                avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
    }).isRequired,
    onDirtyChange: PropTypes.func,
};
